import { gql } from '@apollo/client';
import {
    ALLOY_FIELDS,
    GEM_CLASS_FIELDS,
    GEM_FIELDS,
    RING_PRICING_FIELDS,
    RING_TEMPLATE_FIELDS,
} from './fragments';

export const ALLOYS_QUERY = gql`
  query Alloys {
    alloys {
      ...AlloyFields
    }
  }
  ${ALLOY_FIELDS}
`;

export const ALLOY_QUERY = gql`
  query Alloy($id: ID!) {
    alloy(id: $id) {
      ...AlloyFields
    }
  }
  ${ALLOY_FIELDS}
`;

export const GEMS_QUERY = gql`
  query Gems {
    gems {
      ...GemFields
    }
  }
  ${GEM_FIELDS}
`;

export const GEM_CLASSES_QUERY = gql`
  query GemClasses {
    gemClasses {
      ...GemClassFields
    }
  }
  ${GEM_CLASS_FIELDS}
`;

export const ALLOWED_GEMS_BY_SETTING_TYPE_ID_QUERY = gql`
  query AllowedGemsBySettingTypeId($id: ID!, $width: Float!, $height: Float!) {
    allowedGemsBySettingTypeId(id: $id, width: $width, height: $height) {
      ...GemFields
    }
  }
  ${GEM_FIELDS}
`;

export const SETTING_TYPE_QUERY = gql`
  query SettingType($id: ID!) {
    settingType(id: $id) {
      id
      short
      nameDe
      nameEn
      isSetIntoPartial
      minRingWidth
      minWidthMarkup
      minHeightMarkup
      minGemCarat
      maxGemCarat
      minGemWidth
      maxGemWidth
      minGemLength
      maxGemLength
      maxGemHeight
      descriptionDe
      descriptionEn
      orientation
      styleType
      layoutType
      settingDepths {
        maxGemHeight
        value
      }
      spacings {
        id
        short
        nameDe
        nameEn
        option
        distance
        spacingUnit
      }
      cut {
        id
        short
        nameDe
        nameEn
        shape
      }
    }
  }
`;

export const MAX_GEMS_QUERY = gql`
  query MaxGems(
    $partialSize: Float!
    $partialHeight: Float!
    $settingTypeId: ID!
    $spacingId: ID!
    $gemId: ID!
    $settingDepth: Float!
  ) {
    maxGems(
      partialSize: $partialSize
      partialHeight: $partialHeight
      settingTypeId: $settingTypeId
      spacingId: $spacingId
      gemId: $gemId
      settingDepth: $settingDepth
    )
  }
`;

export const PRODUCT_GEM_QUERY = gql`
  query ProductGem($id: ID!) {
    gem(id: $id) {
      ...GemFields
    }
  }
  ${GEM_FIELDS}
`;

export const PRODUCT_GROUPS_QUERY = gql`
  query ProductGroups($productGroup: [String!], $configurator: Boolean) {
    productGroups(productGroup: $productGroup, configurator: $configurator) {
      id
      index
      short
      nameDe
      nameEn
      descriptionDe
      descriptionEn
      productGroup
      wearType
      nationalMarkup
      internationalMarkup
      configurator
    }
  }
`;

export const PRODUCT_GROUP_QUERY = gql`
  query ProductGroup($id: ID!) {
    productGroup(id: $id) {
      id
      index
      short
      nameDe
      nameEn
      descriptionDe
      descriptionEn
      productGroup
      wearType
      nationalMarkup
      internationalMarkup
      configurator
    }
  }
`;

export const RING_TEMPLATES_QUERY = gql`
  query RingTemplates {
    ringTemplates {
      ...RingTemplateFields
    }
  }
  ${RING_TEMPLATE_FIELDS}
`;

export const RING_TEMPLATES_BY_GROUP_AND_CONFIGURATOR_QUERY = gql`
  query RingTemplatesByGroupAndConfigurator($productGroup: [String!], $configurator: Boolean!) {
    ringTemplatesByGroupAndConfigurator(productGroup: $productGroup, configurator: $configurator) {
      ...RingTemplateFields
    }
  }
  ${RING_TEMPLATE_FIELDS}
`;

export const RING_TEMPLATE_QUERY = gql`
  query RingTemplate($id: ID!) {
    ringTemplate(id: $id) {
      ...RingTemplateFields
    }
  }
  ${RING_TEMPLATE_FIELDS}
`;

export const RING_PRICING_QUERY = gql`
  query RingPricing($calculationTime: NaiveDateTime!, $input: RingInput!) {
    ringPricing(calculationTime: $calculationTime, input: $input) {
      ...RingPricingFields
    }
  }
  ${RING_PRICING_FIELDS}
`;

export const FETCH_DRAFT_ORDER_BY_CODE_QUERY = gql`
  query FetchDraftOrderByCode($draftOrderCode: String!) {
    fetchDraftOrderByCode(draftOrderCode: $draftOrderCode) {
      id
      draftOrderCode
      kaleidoscopeIndex
      kaleidoscopeGroup
      title
      description
      ringStates
      ringImageData
      inKaleidoskope
      visibleInConfigurator
      insertedAt
      updatedAt
      pricing {
        solitaireGemB2b
        ringB2b
        totalB2b
        solitaireGemB2b2c
        eternityGemB2b
        eternityGemB2b2c
        ringB2b2c
        totalB2b2c
      }
    }
  }
`;

export const FETCH_DRAFT_ORDER_QUERY = gql`
  query FetchDraftOrder($id: String!, $draftOrderCode: String!) {
    fetchDraftOrder(id: $id, draftOrderCode: $draftOrderCode) {
      id
      draftOrderCode
      kaleidoscopeIndex
      kaleidoscopeGroup
      title
      description
      ringStates
      ringImageData
      inKaleidoskope
      visibleInConfigurator
      insertedAt
      updatedAt
    }
  }
`;

export const LIST_DRAFT_ORDERS_QUERY = gql`
  query ListDraftOrders($token: String!) {
    listDraftOrders(token: $token) {
      id
      draftOrderCode
      kaleidoscopeIndex
      kaleidoscopeGroup
      title
      description
      ringStates
      ringImageData
      inKaleidoskope
      visibleInConfigurator
      insertedAt
      updatedAt
    }
  }
`;

export const GET_CUSTOMER_ORDER_QUERY = gql`
  query GetCustomerOrder($id: String!, $token: String!) {
    getCustomerOrder(id: $id, token: $token) {
      id
      email
      orderStatus
      orderCode
      orderDate
      weddingDate
      customerNumber
      commissionNumber
      customerCompany
      customerName
      customerSurname
      customerAddressLine
      customerZipCode
      customerCity
      customerState
      expectedDeliveryDate
      annotation
      ringImageDataUri
      rings {
        id
        customerOrderId
        ringId
      }
    }
  }
`;

export const GET_CUSTOMER_ORDER_BY_CODE_QUERY = gql`
  query GetCustomerOrderByCode($orderCode: String!) {
    getCustomerOrderByCode(orderCode: $orderCode) {
      id
      email
      orderStatus
      orderCode
      orderDate
      customerName
      customerSurname
      expectedDeliveryDate
    }
  }
`;

export const GET_CUSTOMER_ORDER_LIST_QUERY = gql`
  query GetCustomerOrderList($token: String!) {
    getCustomerOrderList(token: $token) {
      id
      email
      orderStatus
      orderCode
      orderDate
      customerName
      customerSurname
      expectedDeliveryDate
    }
  }
`;

export const GET_CONFIGURATOR_USER_QUERY = gql`
  query GetConfiguratorUser($token: String!) {
    getConfiguratorUser(token: $token) {
      name
      email
      active
      allowBeta
      allowLabGrown
      logo
      lastLoggedInAt
      customer {
        active
        customerRef
        companyName
        companyShort
        language
      }
    }
  }
`;

export const ACTIVE_SESSIONS_QUERY = gql`
  query ActiveSessions($token: String!) {
    activeSessions(token: $token) {
      userAgent
      deviceInfo
      deviceSeed
      insertedAt
      lastSeenAt
    }
  }
`;

export const GET_CONFIGURATOR_USER_KALEIDOSKOPE_DRAFT_ORDERS_QUERY = gql`
  query GetConfiguratorUserKaleidoskopeDraftOrders($token: String!) {
    getConfiguratorUserKaleidoskopeDraftOrders(token: $token) {
      id
      draftOrderCode
      kaleidoscopeIndex
      kaleidoscopeGroup
      title
      description
      insertedAt
      updatedAt
    }
  }
`;

export const COUNTRIES_QUERY = gql`
  query Countries {
    countries {
      countryRef
      nameDe
      nameEn
      isoAlpha2
      isoAlpha3
      isoNumeric
      erpNumeric
      phoneCountryCode
    }
  }
`;

// Backward-compatible alias used by the current landing page.
export const WEDDING_BANDS_QUERY = RING_TEMPLATES_BY_GROUP_AND_CONFIGURATOR_QUERY;
